import type { Tokens } from "./types";

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
const STORAGE_KEY = "weship.session";

export class ApiError extends Error {
  status: number;
  code: string;
  fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

/**
 * Tokens live in memory first and localStorage second. Reading from memory
 * means a refresh mid-flight does not race against another tab.
 */
let tokens: Tokens | null = readStoredTokens();
let refreshInFlight: Promise<Tokens> | null = null;

function readStoredTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function setTokens(next: Tokens | null) {
  tokens = next;
  if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  else localStorage.removeItem(STORAGE_KEY);
}

export function hasSession() {
  return tokens !== null;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  auth?: boolean;
}

async function send<T>(path: string, options: RequestOptions = {}, retry = true): Promise<T> {
  const { method = "GET", body, idempotencyKey, auth = true } = options;

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth && tokens) headers.Authorization = `Bearer ${tokens.access_token}`;
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // One transparent refresh attempt, then the session is genuinely over.
  if (response.status === 401 && auth && retry && tokens?.refresh_token) {
    try {
      await refreshTokens();
      return send<T>(path, options, false);
    } catch {
      setTokens(null);
      window.dispatchEvent(new CustomEvent("weship:signed-out"));
    }
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? "unknown",
      error.message ?? "Something went wrong. Try again.",
      error.fields ?? {},
    );
  }
  return payload as T;
}

async function refreshTokens(): Promise<Tokens> {
  // Collapse concurrent 401s into a single refresh call.
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const next = await send<Tokens>(
        "/v1/auth/refresh",
        { method: "POST", body: { refresh_token: tokens?.refresh_token }, auth: false },
        false,
      );
      setTokens(next);
      return next;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export const api = {
  get: <T,>(path: string) => send<T>(path),
  post: <T,>(path: string, body?: unknown, idempotencyKey?: string) =>
    send<T>(path, { method: "POST", body, idempotencyKey }),
  patch: <T,>(path: string, body?: unknown) => send<T>(path, { method: "PATCH", body }),
  put: <T,>(path: string, body?: unknown) => send<T>(path, { method: "PUT", body }),
  remove: <T,>(path: string) => send<T>(path, { method: "DELETE" }),
  public: <T,>(path: string) => send<T>(path, { auth: false }),
  // Guest checkout and the landing page estimator post without a session.
  publicPost: <T,>(path: string, body?: unknown) => send<T>(path, { method: "POST", body, auth: false }),
};

/**
 * A booking must carry a stable key across retries, otherwise a double click
 * or a flaky connection books two collections and bills the customer twice.
 */
export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `ws-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
